import React, {useCallback, useState} from "react";
import QTextBox from "../../../../common/components/QTextBox";
import {ResponseInterface} from "../../../../interfaces/ResponseInterface";
import {config} from "../../../../config";
import {Col, Row} from "reactstrap";
import {Formik} from "formik";
import QButton from "../../../../common/components/QButton";
import {useDispatch} from "react-redux";
import {setToast} from "../../../../app/rootReducer";
import SetupApi from "../../../../common/api/functions/Setup";
import {SetupInterface} from "../../../../interfaces/private/SetupInterface";
import {useTranslation} from "react-i18next";
import QSelect from "../../../../common/components/QSelect";
import {SelectOptionInterface} from "../../../../interfaces/SelectOptionInterface";
import {
    CustomerGroupInterface,
    LayoutInterface,
    PaymentTermInterface,
    ProductGroupInterface,
    ProductInterface,
    VatZoneInterface,
} from "../../../../interfaces/economic/CommonInterface";
import QSelectSearch from "../../../../common/components/QSelectSearch";
import {debounce} from "lodash";
import {Input, Label} from "reactstrap";
import InvoiceDefaultsGroup from "../../../public/SetupV2/components/InvoiceDefaultsGroup";
import {applyInvoiceSetupDefaults} from "../../../public/SetupV2/helpers/invoiceDefaults";

interface Props {
    model: SetupInterface,
    setOpen: Function,
    setLoadingFunc: Function,
    setSetup: Function,
    setup: SetupInterface,
    allDataSetup: any,
}

const BodyInvoiceSetupComponent: React.FC<Props> = ({allDataSetup, setOpen, setLoadingFunc, setSetup, setup}) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
        vat: true,
        document: false,
        booking: false,
        common: false,
    });

    const {
        layouts = [],
        paymentTerms = [],
        bookedWiths = {},
        otherRef = {},
        invoiceSources = {
            invoice: 'Stripe Invoice',
            checkout: 'Stripe Checkout',
        },
        vatZones = [],
        customerGroups = [],
        productGroups = [],
    } = allDataSetup || {};

    const toggleGroup = (key: string) => {
        setOpenGroups((prev) => ({...prev, [key]: !prev[key]}));
    };

    const promiseOptions = useCallback(debounce((e: string, callback) => {
        if (e.length > 1) {
            SetupApi.searchProduct(e).then(async (response: ResponseInterface | any) => {
                return response?.data?.data?.map((item: ProductInterface) => ({
                    label: item.productNumber + ' - ' + item.name,
                    value: item.productNumber,
                }))
            }).then(results => {
                callback(results);
            });
        } else {
            callback(null);
        }
    }, 500), []);

    return (
        <Formik
            initialValues={applyInvoiceSetupDefaults({
                ...setup,
                stripe_invoice_source: setup.stripe_invoice_source ?? 'invoice',
                stripe_checkout_prices_include_vat: !!setup.stripe_checkout_prices_include_vat,
                stripe_checkout_included_vat_rate: setup.stripe_checkout_included_vat_rate ?? 25,
                economic_invoice_send_out: !!setup.economic_invoice_send_out,
            }, {
                layouts,
                paymentTerms,
            })}
            onSubmit={(values: any) => {
                SetupApi.update(values).then(async (response: ResponseInterface | any) => {
                    if (response.status === config.STATUS_CODE.OK) {
                        setSetup(response?.data?.data || {});
                        await setOpen(false);
                        await setLoadingFunc(true);
                        dispatch(setToast({
                            message: t("economic.updateSuccess"),
                            type: config.TOAST_TYPE.SUCCESS
                        }));
                    }
                });
            }}
        >
            {formik => (
                <React.Fragment>
                    <QSelect
                        label={t("economic.invoiceSource")}
                        value={formik.values.stripe_invoice_source}
                        onChange={(option: SelectOptionInterface) => {
                            formik.setFieldValue('stripe_invoice_source', option.value);
                        }}
                        options={Object.entries(invoiceSources).map(([key, value]) => ({
                            label: value as string,
                            value: key,
                        }))}
                        error={formik.errors?.stripe_invoice_source}
                        menuPortal
                    />
                    <div className="alert alert-light border mb-3">
                        <Label className="d-block mb-1">
                            {t("economic.checkoutPricesIncludeVat")}
                            <Input
                                style={{marginLeft: '10px'}}
                                type="checkbox"
                                checked={!!formik.values.stripe_checkout_prices_include_vat}
                                onChange={(e) => {
                                    formik.setFieldValue('stripe_checkout_prices_include_vat', e.target.checked);
                                }}
                            />
                        </Label>
                        <small className="text-muted d-block mb-2">
                            {t("economic.checkoutPricesIncludeVatHelp")}
                        </small>
                        {formik.values.stripe_checkout_prices_include_vat && (
                            <>
                                <div className="alert alert-warning py-2 mb-2" role="status">
                                    {t("economic.checkoutVatHint")}
                                </div>
                                <QTextBox
                                    label={t("economic.checkoutIncludedVatRate")}
                                    value={formik.values.stripe_checkout_included_vat_rate}
                                    onChange={(event: any) => {
                                        formik.setFieldValue('stripe_checkout_included_vat_rate', event);
                                    }}
                                    error={formik.errors?.stripe_checkout_included_vat_rate}
                                />
                            </>
                        )}
                    </div>

                    <InvoiceDefaultsGroup
                        title={t("common.setup.invoiceSectionVat")}
                        help={t("common.setup.invoiceVatHelp")}
                        open={openGroups.vat}
                        onToggle={() => toggleGroup('vat')}
                    >
                        {[
                            ['economic_invoice_vat_zone_domestic_number', 'economic.vatZoneDomestic'],
                            ['economic_invoice_vat_zone_eu_number', 'economic.vatZoneEu'],
                            ['economic_invoice_vat_zone_abroad_number', 'economic.vatZoneAbroad'],
                            ['economic_invoice_vat_zone_domestic_without_vat_number', 'economic.vatZoneDomesticWithoutVat'],
                        ].map(([field, label]) => (
                            <QSelect
                                key={field}
                                menuPortal
                                label={t(label)}
                                value={(formik.values as any)[field]}
                                onChange={(option: SelectOptionInterface) => {
                                    formik.setFieldValue(field, option.value);
                                }}
                                options={vatZones.map((zone: VatZoneInterface) => ({
                                    label: zone.name,
                                    value: zone.vatZoneNumber,
                                }))}
                                error={(formik.errors as any)?.[field]}
                            />
                        ))}
                    </InvoiceDefaultsGroup>

                    <InvoiceDefaultsGroup
                        title={t("common.setup.invoiceSectionDocument")}
                        help={t("common.setup.invoiceSectionDocumentHelp")}
                        open={openGroups.document}
                        onToggle={() => toggleGroup('document')}
                    >
                        <QSelect
                            menuPortal
                            label={t("economic.layout")}
                            value={formik.values.economic_invoice_layout_number}
                            onChange={(option: SelectOptionInterface) => {
                                formik.setFieldValue('economic_invoice_layout_number', option.value);
                            }}
                            options={layouts.map((a: LayoutInterface) => ({label: a.name, value: a.layoutNumber}))}
                            error={formik.errors?.economic_invoice_layout_number}
                        />
                        <QSelect
                            menuPortal
                            label={t("economic.paymentTerms")}
                            value={formik.values.economic_invoice_payment_terms_number}
                            onChange={(option: SelectOptionInterface) => {
                                formik.setFieldValue('economic_invoice_payment_terms_number', option.value);
                            }}
                            options={paymentTerms.map((a: PaymentTermInterface) => ({
                                label: a.name,
                                value: a.paymentTermsNumber
                            }))}
                            error={formik.errors?.economic_invoice_payment_terms_number}
                        />
                    </InvoiceDefaultsGroup>

                    <InvoiceDefaultsGroup
                        title={t("common.setup.invoiceSectionBooking")}
                        help={t("common.setup.invoiceSectionBookingHelp")}
                        open={openGroups.booking}
                        onToggle={() => toggleGroup('booking')}
                    >
                        <QSelect
                            menuPortal
                            label={t("economic.bookedWith")}
                            value={formik.values.economic_invoice_booked_with}
                            onChange={(option: SelectOptionInterface) => {
                                formik.setFieldValue('economic_invoice_booked_with', option.value);
                            }}
                            options={Object.entries(bookedWiths).map(([key, value]) => ({
                                label: value as string,
                                value: key
                            }))}
                            error={formik.errors?.economic_invoice_booked_with}
                        />
                        <p className="text-muted small mb-0">
                            {String(formik.values.economic_invoice_booked_with) === '2'
                                ? t("economic.bookedWithAutoHelp")
                                : t("economic.bookedWithManualHelp")}
                        </p>
                        {String(formik.values.economic_invoice_booked_with) === '2' && (
                            <div className="mt-2">
                                <Label className="d-block mb-1">
                                    {t("economic.sendOutInvoice")}
                                    <Input
                                        style={{marginLeft: '10px'}}
                                        type="checkbox"
                                        checked={!!formik.values.economic_invoice_send_out}
                                        onChange={(e) => {
                                            formik.setFieldValue('economic_invoice_send_out', e.target.checked);
                                        }}
                                    />
                                </Label>
                                <p className="text-muted small mb-0">
                                    {t("economic.sendOutInvoiceHelp")}
                                </p>
                            </div>
                        )}
                        <QSelect
                            menuPortal
                            label={t("economic.otherRef")}
                            value={formik.values.economic_invoice_other_ref}
                            onChange={(option: SelectOptionInterface) => {
                                formik.setFieldValue('economic_invoice_other_ref', option.value);
                            }}
                            options={Object.entries(otherRef).map(([key, value]) => ({
                                label: value as string,
                                value: key
                            }))}
                            error={formik.errors?.economic_invoice_other_ref}
                        />
                        <QSelectSearch
                            menuPortal
                            label={t("economic.productDiscount")}
                            value={formik.values.economic_invoice_product_discount_number}
                            onChange={(option: SelectOptionInterface) => {
                                formik.setFieldValue('economic_invoice_product_discount_number', option.value);
                            }}
                            error={formik.errors?.economic_invoice_product_discount_number}
                            placeholder={t("economic.productDiscountPlaceholder")}
                            promiseOptions={promiseOptions}
                        />
                    </InvoiceDefaultsGroup>

                    <InvoiceDefaultsGroup
                        title={t("common.setup.invoiceSectionCommon")}
                        help={t("common.setup.invoiceCommonHelp")}
                        open={openGroups.common}
                        onToggle={() => toggleGroup('common')}
                    >
                        <QSelect
                            menuPortal
                            label={t("economic.customerGroup")}
                            value={formik.values.economic_customer_group_number}
                            onChange={(option: SelectOptionInterface) => {
                                formik.setFieldValue('economic_customer_group_number', option.value);
                            }}
                            options={customerGroups.map((a: CustomerGroupInterface) => ({
                                label: a.name,
                                value: a.customerGroupNumber
                            }))}
                            error={formik.errors?.economic_customer_group_number}
                        />
                        <QSelect
                            menuPortal
                            label={t("economic.customerPaymentTerms")}
                            value={formik.values.economic_customer_payment_terms_number}
                            onChange={(option: SelectOptionInterface) => {
                                formik.setFieldValue('economic_customer_payment_terms_number', option.value);
                            }}
                            options={paymentTerms.map((a: PaymentTermInterface) => ({
                                label: a.name,
                                value: a.paymentTermsNumber
                            }))}
                            error={formik.errors?.economic_customer_payment_terms_number}
                        />
                        <QSelect
                            menuPortal
                            label={t("economic.productGroup")}
                            value={formik.values.economic_product_group_number}
                            onChange={(option: SelectOptionInterface) => {
                                formik.setFieldValue('economic_product_group_number', option.value);
                            }}
                            options={productGroups.map((a: ProductGroupInterface) => ({
                                label: a.name,
                                value: a.productGroupNumber
                            }))}
                            error={formik.errors?.economic_product_group_number}
                        />
                    </InvoiceDefaultsGroup>

                    <Row>
                        <Col>
                            <div className="d-flex justify-content-end gap-2 mt-2">
                                <QButton
                                    type="default"
                                    onClick={() => setOpen(false)}
                                    content={t("common.button.cancel")}
                                />
                                <QButton
                                    type="primary"
                                    onClick={() => formik.handleSubmit()}
                                    content={t("common.button.submit")}
                                />
                            </div>
                        </Col>
                    </Row>
                </React.Fragment>
            )}
        </Formik>
    );
};

export default BodyInvoiceSetupComponent;
